import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import { useIntl } from "react-intl";

import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import { Button } from "@mapmycustomers/ui";

import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import { areLayoutsRolesAssociationsLoading, getLayoutsRolesAssociations } from "../../store";
import { showLayoutRolesAssociation } from "../../store/actions";
import { FormLayoutRoleMap } from "../../type/FormLayoutRoleMap";
import AssociationsList from "../AssociationsList";

import styles from "./AssociationsCell.module.scss";

interface Props {
  analyticsIssuer?: AnalyticsService;
  associations: FormLayoutRoleMap;
  id: FormLayout["id"];
  loading: boolean;
  showPanel: typeof showLayoutRolesAssociation;
}

const AssociationsCell: React.FC<Props> = ({
  id,
  analyticsIssuer,
  associations,
  loading,
  showPanel,
}) => {
  const intl = useIntl();

  const [popoverVisible, setPopoverVisible] = useState(false);

  const layoutAssociations = associations?.get(id);
  const hasAssociations = Array.isArray(layoutAssociations) && layoutAssociations?.length > 0;

  const handleAssignClick = useCallback(() => {
    analyticsIssuer?.clicked(["Individual Layout", "Assign Layout"]);

    setPopoverVisible(false);
    showPanel();
  }, [analyticsIssuer, setPopoverVisible, showPanel]);

  const handleVisibleChange = useCallback((visible) => setPopoverVisible(visible), []);

  if (loading) {
    return null;
  }

  if (!hasAssociations) {
    return (
      <Button onClick={handleAssignClick} size="small" type="filled">
        {intl.formatMessage({
          id: "settings.formLayouts.grid.actions.roles.assign",
          defaultMessage: "Assign",
          description: "Title of the Assign button in the Used By column",
        })}
      </Button>
    );
  }

  return (
    <Popover
      content={
        <div className={styles.overlay}>
          <AssociationsList associations={layoutAssociations} />

          <div className={styles.footer}>
            <Button onClick={handleAssignClick} size="small">
              {intl.formatMessage({
                id: "settings.formLayouts.grid.actions.roles.editLayouts",
                defaultMessage: "Edit Layouts",
                description: "Title of the Edit Layouts button in the Used By column popover",
              })}
            </Button>
          </div>
        </div>
      }
      destroyTooltipOnHide
      onVisibleChange={handleVisibleChange}
      overlayClassName={styles.overlayContainer}
      placement="bottomRight"
      trigger={["click"]}
      visible={popoverVisible}
    >
      <Button
        className={styles.btn}
        rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
        type="text"
      >
        {intl.formatMessage(
          {
            id: "settings.formLayouts.grid.actions.roles.count",
            defaultMessage: "{count} {count, plural, one {Role} other {Roles}}",
            description: "Label with counter of associated roles",
          },
          {
            count: layoutAssociations.length,
          }
        )}
      </Button>
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => ({
  associations: getLayoutsRolesAssociations(state),
  loading: areLayoutsRolesAssociationsLoading(state),
});

const mapDispatchToProps = {
  showPanel: showLayoutRolesAssociation,
};

export default connect(mapStateToProps, mapDispatchToProps)(AssociationsCell);
